import { ComponentProps, FunctionComponent, useRef } from 'react';
import { PropsWithChildren, forwardRef, ForwardedRef, ReactNode } from 'react';
import { Box, BoxProps, BoxType } from 'reflexbox';
import styled from 'styled-components';
import { useIntersectionObserver } from 'usehooks-ts';
import { rgba } from 'polished';
import { Separator } from './Separator';

function ContainerWithoutRef(
	props: PropsWithChildren<BoxProps & { wide?: number | boolean }>,
	ref: ForwardedRef<HTMLDivElement>,
) {
	return (
		<Box ref={ref} px={props.wide ? 4 : [3, 4, 5]} {...(props as ComponentProps<BoxType>)}>
			<Box
				maxWidth={props.wide ? (typeof props.wide === 'number' ? props.wide : undefined) : 800}
				marginX="auto"
			>
				{props.children}
			</Box>
		</Box>
	);
}

export const Container = forwardRef(ContainerWithoutRef) as unknown as FunctionComponent<
	PropsWithChildren<BoxProps & { wide?: number | boolean }>
> & { Sticky: typeof ContainerSticky };

const StickyBorder = styled(Box)`
	transition: 1s cubic-bezier(0, 1, 0, 1) opacity;
	opacity: 0;
`;

const Shadow = styled(Box)`
	position: absolute;
	top: 100%;
	width: 100%;
	height: 8px;
	background: linear-gradient(
		${(props) => rgba(props.theme.colors.lightBlue, 0.5)} 0%,
		${(props) => rgba(props.theme.colors.lightBlue, 0.25)} 25%,
		${(props) => rgba(props.theme.colors.lightBlue, 0.0)} 100%
	);
`;

const Sticky = styled(Box)<{ floating?: boolean }>`
	position: sticky;
	top: 0;
	background: white;
	z-index: 1;

	${StickyBorder} {
		opacity: ${(props) => (props.floating ? 1 : 0)};
	}
`;

function ContainerSticky(props: PropsWithChildren<{ py?: number; header?: ReactNode }>) {
	const ref = useRef<HTMLDivElement>(null);

	const observer = useIntersectionObserver(ref, {
		threshold: 1,
	});

	return (
		<Box ref={ref}>
			{props.header && (
				<Sticky floating={observer?.isIntersecting === false}>
					<Container py={props.py}>{props.header}</Container>
					<StickyBorder>
						<Separator />
						<Shadow />
					</StickyBorder>
				</Sticky>
			)}
			<Container pb={props.py}>{props.children}</Container>
		</Box>
	);
}

Container.Sticky = ContainerSticky;
